<template>
  <div class="pg-syl">
    <!-- 섹션 레벨: 제목 -->
    <div class="p-field">
      <label class="p-field__label">Section Title</label>
      <input
        v-model="sectionData.content.sectionTitle"
        type="text"
        class="p-input"
        placeholder="섹션 제목"
      />
    </div>

    <!-- 문항 탭 -->
    <question-tabs
      :count="questions.length"
      v-model="currentIndex"
      @add="addQuestion"
      @remove="removeQuestion"
    />

    <template v-if="current">
      <div class="p-form-grid p-form-grid--2">
        <div class="p-field">
          <label class="p-field__label">Question / 지시문</label>
          <input
            v-model="current.questionText"
            type="text"
            class="p-input"
            placeholder="예: 음성을 듣고 빈칸을 채워 단어를 완성하세요"
          />
        </div>
        <div class="p-field">
          <label class="p-field__label">
            <i class="material-icons-round pg-syl__audio-ico">music_note</i>
            Audio URL (문제별 단어 발음)
          </label>
          <input
            v-model="current.audioUrl"
            type="text"
            class="p-input"
            placeholder="음성 URL (풀 URL 권장)"
          />
        </div>
      </div>

      <!-- 단어 미리보기 -->
      <div class="pg-syl__preview">
        <span class="pg-syl__preview-label">미리보기</span>
        <div class="pg-syl__preview-word">
          <template v-if="current.syllables.length">
            <span
              v-for="(s, i) in current.syllables"
              :key="i"
              class="pg-syl__preview-syl"
              :class="{ 'is-blank': s.blank }"
            >{{ s.blank ? "□" : (s.char || "·") }}</span>
          </template>
          <span v-else class="pg-syl__preview-empty">음절을 추가하세요</span>
        </div>
      </div>

      <!-- 음절 슬롯 편집 -->
      <div class="pg-syl__block">
        <div class="pg-syl__block-head">음절 슬롯 (단어 구성)</div>
        <div class="pg-syl__slots">
          <div
            v-for="(slot, index) in current.syllables"
            :key="index"
            class="pg-syl__slot"
            :class="{ 'is-blank': slot.blank }"
          >
            <span class="pg-syl__slot-idx">{{ index + 1 }}</span>
            <input
              v-model="slot.char"
              type="text"
              class="p-input pg-syl__slot-input"
              placeholder="음절"
            />
            <label class="pg-syl__toggle">
              <input type="checkbox" v-model="slot.blank" />
              <span>빈칸(정답)</span>
            </label>
            <button
              type="button"
              class="pg-syl__icon-btn pg-syl__icon-btn--danger"
              title="제거"
              @click.prevent="removeSyllable(index)"
            >
              <i class="material-icons-round">delete</i>
            </button>
          </div>
        </div>
        <div class="pg-syl__actions">
          <ABtn kind="yellow" icon="add" @click.prevent="addSyllable">음절 추가</ABtn>
        </div>
      </div>

      <!-- 오답 카드 -->
      <div class="pg-syl__block">
        <div class="pg-syl__block-head">
          오답 카드 (distractors)
          <span class="pg-syl__block-sub">정답 음절은 빈칸 슬롯에 내장됩니다. 여기엔 오답 카드만 입력하세요.</span>
        </div>
        <div class="pg-syl__chips">
          <div
            v-for="(d, index) in current.distractors"
            :key="index"
            class="pg-syl__chip"
          >
            <input
              v-model="current.distractors[index]"
              type="text"
              class="p-input pg-syl__chip-input"
              placeholder="오답 음절"
            />
            <button
              type="button"
              class="pg-syl__chip-x"
              title="제거"
              @click.prevent="removeDistractor(index)"
            >
              <i class="material-icons-round">close</i>
            </button>
          </div>
        </div>
        <div class="pg-syl__actions">
          <ABtn kind="ghost" icon="add" @click.prevent="addDistractor">오답 카드 추가</ABtn>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import ABtn from "@/components/Pineapple/ABtn.vue";
import QuestionTabs from "@/components/Practice/QuestionTabs.vue";
import { useQuestions } from "@/components/Practice/useQuestions";

const sectionData = defineModel({ type: Object, required: true });

const makeEmpty = () => ({
  questionText: "",
  audioUrl: "",
  syllables: [{ char: "", blank: false }],
  distractors: []
});

const { questions, currentIndex, current, addQuestion, removeQuestion } = useQuestions(
  sectionData,
  makeEmpty,
  ["questionText", "syllables", "distractors", "audioUrl"]
);

const addSyllable = () => {
  current.value.syllables.push({ char: "", blank: false });
};

const removeSyllable = (index) => {
  current.value.syllables.splice(index, 1);
};

const addDistractor = () => {
  current.value.distractors.push("");
};

const removeDistractor = (index) => {
  current.value.distractors.splice(index, 1);
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.pg-syl {
  display: flex;
  flex-direction: column;
  gap: 16px;

  &__audio-ico {
    font-size: 14px !important;
    color: $p-point-deep;
    vertical-align: middle;
    margin-right: 4px;
  }

  // ── 미리보기 ──
  &__preview {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 14px 16px;
    border: 1px dashed $p-line-hard;
    border-radius: $p-radius-md;
    background: $p-bg;
  }

  &__preview-label {
    font-size: 10.5px;
    font-weight: 800;
    letter-spacing: 0.05em;
    color: $p-ink-soft;
    text-transform: uppercase;
    flex-shrink: 0;
  }

  &__preview-word {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }

  &__preview-syl {
    min-width: 40px;
    height: 40px;
    padding: 0 8px;
    display: inline-grid;
    place-items: center;
    border-radius: $p-radius-sm;
    border: 1.5px solid $p-line;
    background: $p-panel;
    font-size: 18px;
    font-weight: 700;
    color: $p-ink;

    &.is-blank {
      border-style: dashed;
      border-color: $p-point-deep;
      background: $p-point-soft;
      color: $p-point-deep;
    }
  }

  &__preview-empty {
    font-size: 13px;
    color: $p-ink-soft;
  }

  // ── 블록 ──
  &__block {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 14px 16px;
    border: 1px solid $p-line;
    border-radius: $p-radius-md;
    background: $p-panel;
  }

  &__block-head {
    font-size: 13px;
    font-weight: 800;
    color: $p-ink;
    display: flex;
    align-items: baseline;
    gap: 8px;
    flex-wrap: wrap;
  }

  &__block-sub {
    font-size: 11.5px;
    font-weight: 500;
    color: $p-ink-soft;
  }

  // ── 음절 슬롯 ──
  &__slots {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__slot {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 10px;
    border: 1px solid $p-line;
    border-radius: $p-radius-sm;
    background: $p-bg;

    &.is-blank {
      border-color: $p-point-deep;
      background: $p-point-soft;
    }
  }

  &__slot-idx {
    min-width: 24px;
    height: 24px;
    display: inline-grid;
    place-items: center;
    border-radius: 999px;
    background: $p-ink;
    color: $p-point;
    font-family: $p-font-mono;
    font-size: 11px;
    font-weight: 800;
    flex-shrink: 0;
  }

  &__slot-input {
    max-width: 120px;
  }

  &__toggle {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 12px;
    font-weight: 700;
    color: $p-ink-mid;
    cursor: pointer;
    user-select: none;
    white-space: nowrap;
  }

  // ── 오답 카드 칩 ──
  &__chips {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  &__chip {
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }

  &__chip-input {
    max-width: 110px;
  }

  &__chip-x {
    width: 26px;
    height: 26px;
    border: 0;
    background: transparent;
    color: $p-ink-mid;
    border-radius: $p-radius-sm;
    display: grid;
    place-items: center;
    cursor: pointer;

    > i { font-size: 15px; }

    &:hover { background: $p-err-soft; color: $p-err; }
  }

  &__icon-btn {
    width: 30px;
    height: 30px;
    border: 0;
    background: transparent;
    color: $p-ink-mid;
    border-radius: $p-radius-sm;
    display: grid;
    place-items: center;
    cursor: pointer;
    transition: background $p-dur $p-ease, color $p-dur $p-ease;
    margin-left: auto;

    > i { font-size: 16px; }

    &--danger {
      color: $p-err;
      &:hover { background: $p-err-soft; }
    }
  }

  &__actions {
    display: flex;
    gap: 8px;
  }
}
</style>
